import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { HideLoader, ShowLoader } from "../../../store/loader/loader.actions";
import { Store } from "@ngxs/store";
import { ResourceCountry, ResourceProvinces } from '../../../../models/resources/resources.model';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { AddressHelper } from '../../../../helper/address.helper';
import {
  Articles,
  ConstructionTypes,
  EditLeadDialogData, Keyword,
  LeadDataObject,
  LeadsDataContactObject, LeadSources,
  LeadType
} from '../../../../models/leads/leads.model';
import { combineLatest, forkJoin, from, concatMap, tap, Observable } from 'rxjs';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { ValidatorHelper } from '../../../../helper/validator.helper';
import { LeadHelper } from '../../../../helper/lead.helper';
import { LOCALE_KEY } from '../../../../services/user-service/user-token.service';
import { ContactHelper } from '../../../../helper/contact.helper';
import { UserProfileShareService } from '../../../../services/shared-service/user-profile-share.service';

@Component({
  selector: 'fi-edit-lead',
  templateUrl: './edit-lead.component.html',
  styleUrls: ['./edit-lead.component.scss'],
  standalone: false,
})

export class EditLeadComponent extends BaseCustomDialog {
  leadTypeOffer = LeadHelper.LEAD_TYPE_OFFER;
  leadTypeBrochure = LeadHelper.LEAD_TYPE_BROCHURE;
  leadTypeMuster = LeadHelper.LEAD_TYPE_MUSTER;
  leadTypeAusschreibungstexte = LeadHelper.LEAD_TYPE_AUSSCHREIBUNGSTEXTE;
  leadTypeCadDaten = LeadHelper.LEAD_TYPE_CAD_DATEN;
  defaultConstructionTypeId = 3;
  leadTypeRelationshipManagement = LeadHelper.LEAD_TYPE_PLANER_RELATIONSHIP;
  leadNumber = '';

  leadForm = this.fb.group({
    Commission: this.fb.control(''),
    Address: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    ZipCode: this.fb.control('', Validators.pattern(ValidatorHelper.zipValidator)),
    City: this.fb.control('', Validators.pattern(ValidatorHelper.cityValidator)),
    CountryCode: this.fb.control(''),
    ProvinceCode: this.fb.control(''),

    LeadTypeId: this.fb.control(-1, Validators.required),
    LeadSourceId: this.fb.control(-1, Validators.required),

    ConstructionTypeId: this.fb.control(this.defaultConstructionTypeId),

    PLANER_CODE: this.fb.control('', Validators.pattern(ValidatorHelper.planerNumberValidator)),
  });

  resourceCountries: ResourceCountry[];
  resourceProvinces: ResourceProvinces[];
  provinceCountryCodes: string[] = [];
  shownPossibleTags: Keyword[] = [];
  tags: Keyword[] = [];
  leadTypes: LeadType[];
  leadSources: LeadSources[];
  constructionTypes: ConstructionTypes[];
  sameAsContactAddress = false;
  hasError: boolean = false;
  canEditFields: boolean = true;
  hasSampleAccessError: boolean = false;
  isShared: boolean = false;
  showSharedLeadBox: boolean;
  isDealerPartner: boolean;
  isPremiumProjectPartner: boolean;

  brochures: { value: string, text: string, image: string }[];
  ausschreibungstextes: { value: string, text: string, image: string }[];
  cadDatens: { value: string, text: string, image: string }[];
  musters: { value: string, text: string }[];
  selectedItems: { value: string, text: string, id?: number }[] = [];

  broschureLabelText: string = '';
  musterLabelText: string = '';
  ausschreibungstexteLabelText: string = '';
  cadDateLabelText: string = '';

  private selectedLeadTypeId: number;
  private loadedLeadTypes = [];
  private allPossibleTags: Keyword[] = [];
  private oldTags = new Set<Keyword>();
  private tagsToAdd: Keyword[] = [];
  private tagsToDelete: Keyword[] = [];
  private oldLeadType: number;
  private oldLeadSource: number;
  private oldIsShared: boolean;
  private oldPlanerNumber: string;
  private oldCommission: string;
  private oldConstructionTypeId: number;
  private previousArticles: Articles[] = [];
  private articlesThatRemovedOnSubmit: Articles[] = [];
  private articleKeyLeadTypeIds = new Map<string, number>();
  private lead: LeadDataObject;

  constructor(
    public dialogRef: MatDialogRef<EditLeadComponent>,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    @Inject(MAT_DIALOG_DATA) public dialogData: EditLeadDialogData,
    private readonly sharedProfileService: UserProfileShareService,
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
    super(dialogRef);

    AddressHelper.getCountries().then(data => {
      this.resourceCountries = data;
    });

    AddressHelper.getProvinceCountryCodes().then(data => {
      this.provinceCountryCodes = data;
    });

    // When false the dialog runs in "share only" mode: every field is read only and the
    // "share with Finstral" option is the only thing that can be changed and submitted.
    this.canEditFields = this.dialogData.canEditFields ?? false;

    this.sharedProfileService.getIsDealerPartner().subscribe(isDealerPartner => {
      this.isDealerPartner = isDealerPartner;
    });

    this.sharedProfileService.getIsPremiumProjectPartner().subscribe(isPremiumProjectPartner => {
      this.isPremiumProjectPartner = isPremiumProjectPartner;
    });

    leadsDataService.getConstructionTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        this.constructionTypes = data.filter(item => !item.Inactive);
      }
    });

    leadsDataService.getLeadTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        this.loadedLeadTypes = data.filter(item => !item.Inactive);
        this.updateLeadTypesDropdown();
        this.setDynamicLabelKeys();
      }
    });

    leadsDataService.getLeadSources().subscribe(( { success, data } ) => {
      if ( success ) {
        if (this.isDealerPartner) {
          this.leadSources = data.filter(item => !item.Inactive && !item.FinstralOnly);
        } else {
          this.leadSources = data.filter(item => !item.Inactive);
        }
      }
    });

    leadsDataService.getAllKeywords().subscribe(( { success, data } ) => {
      if ( success ) {
        this.tags = [...dialogData.tags];
        this.oldTags = new Set<Keyword>(dialogData.tags);
        this.shownPossibleTags = this.allPossibleTags = this.convertStringToKeyword(data);
        this.updateTags();
      }
    });

    const locale = sessionStorage.getItem(LOCALE_KEY);

    this.brochures = dialogData.broschures.map(( item ) => {
      this.articleKeyLeadTypeIds.set('broch_' + item.key, LeadHelper.LEAD_TYPE_BROCHURE)
      return {
        text: item.title + '\n' + item.ordernumber,
        value: item.key,
        image: item.img
      };
    });

    this.ausschreibungstextes = dialogData.ausschreibungstextes.map(( item ) => {
      this.articleKeyLeadTypeIds.set('aussch_' + item.key, LeadHelper.LEAD_TYPE_AUSSCHREIBUNGSTEXTE)
      return {
        text: item.title + (item.ordernumber ? '\n' + item.ordernumber : ''),
        value: item.key,
        image: 'none'
      };
    });

    this.cadDatens = dialogData.cadDatens.map(( item ) => {
      this.articleKeyLeadTypeIds.set('cad_' + item.key, LeadHelper.LEAD_TYPE_CAD_DATEN)
      return {
        text: item.title + (item.ordernumber ? '\n' + item.ordernumber : ''),
        value: item.key,
        image: 'none'
      };
    });

    this.musters = dialogData.musters.map(( item ) => {
      this.articleKeyLeadTypeIds.set('must_' + item.key, LeadHelper.LEAD_TYPE_MUSTER)
      return {
        text: item.title,
        value: item.key,
        image: item.img,
      };
    });

    store.dispatch(new ShowLoader());

    combineLatest([
      leadsDataService.getLeadById(dialogData.leadId),
      leadsDataService.getExtendedPropertiesByLeadId(dialogData.leadId),
    ]).subscribe(datas => {
      store.dispatch(new HideLoader());

      if ( datas[0].success ) {
        this.lead = datas[0].data.Lead;
        this.selectedLeadTypeId = this.lead.LeadTypeId;
        this.isShared = datas[0].data.Lead.IsShared;
        this.oldIsShared = datas[0].data.Lead.IsShared;

        if ( this.lead?.Contact && datas[0].data.Contact ) {
          Object.assign(this.lead.Contact, datas[0].data.Contact);
        }


        this.leadNumber = datas[0].data.Lead?.DateFormattedId;
        this.leadForm.patchValue({
          ...datas[0].data.Lead?.Address,
          Commission: datas[0].data.Lead?.Commission,
          ConstructionTypeId: datas[0].data.Lead?.ConstructionTypeId,
        });

        this.checkSharedLead(this.lead.Contact);
        this.onCountryChange(datas[0].data.Lead?.Address?.CountryCode ?? '');
        this.setLeadTypeId(datas[0].data.Lead?.LeadTypeId, false);
        this.setLeadSourceId(datas[0].data.Lead?.LeadSourceId);
        this.setSelectedArticles(datas[0].data.Articles, datas[0].data.Lead?.LeadTypeId);

        if ( datas[1].success ) {
          this.leadForm.controls.PLANER_CODE.setValue(datas[1].data.PLANER_CODE);
        }


        this.updateLeadTypesDropdown();
      }


      const body = this.leadForm.getRawValue();

      this.oldConstructionTypeId = body.ConstructionTypeId;
      this.oldLeadType = body.LeadTypeId;
      this.oldLeadSource = body.LeadSourceId;
      this.oldCommission = body.Commission;
      this.oldPlanerNumber = body.PLANER_CODE;

      for ( const name of ['Address', 'ZipCode', 'City', 'CountryCode'] ) {
        if ( this.leadForm.controls[name].value ) {
          return;
        }
      }

      this.sameAsContactAddress = true;
      this.onSameAsContactAddressChange();
    });


  }


  onClosePopup( message?: string ): void {
    this.clearClassAndCloseDialog(this.dialogRef, message);
  }

  setLeadTypeId( typeId: number, reset: boolean ): void {
    this.hasError = false;
    this.hasSampleAccessError = false;
    this.selectedLeadTypeId = typeId;

    if ( reset ) {
      this.selectedItems = [];
    }

    if ( typeId !== this.leadTypeOffer ) {
      this.leadForm.controls.ConstructionTypeId.reset();
      this.leadForm.controls.PLANER_CODE.reset();
    } else if ( !this.leadForm.controls.ConstructionTypeId.value ) {
      this.setConstructionTypeId(this.defaultConstructionTypeId);
    }

    this.leadForm.controls.LeadTypeId.setValue(typeId);
  }

  setLeadSourceId( sourceId: number ): void {
    this.leadForm.controls.LeadSourceId.setValue(sourceId);
  }

  setConstructionTypeId( eventTypeId: number ): void {
    this.leadForm.controls.ConstructionTypeId.setValue(eventTypeId);
  }

  onAddTag( data: string ): void {
    this.tags.push({ Text: data, Owned: true });
    this.updateTags();
  }

  updateTags(): void {
    // 1. Exact equivalent: Remove empty tags and deduplicate (based on text)
    this.tags = [...new Map(
      this.tags
        .filter(tag => tag.Text.trim())  // Use tag.text instead of tag
        .map(tag => [tag.Text, tag])     // Use text as key for deduplication
    ).values()];

    // 2. Exact equivalent: Find tags to add (present in current but not in old)
    this.tagsToAdd = this.tags.filter(tag =>
      ![...this.oldTags].some(oldTag => oldTag.Text === tag.Text)  // Compare by text
    );

    // 3. Exact equivalent: Find tags to delete (present in old but not in current)
    this.tagsToDelete = [...this.oldTags].filter(oldTag =>
      !this.tags.some(tag => tag.Text === oldTag.Text)  // Compare by text
    );

    // 4. Exact equivalent: Filter available tags (all possible except current)
    this.shownPossibleTags = this.allPossibleTags.filter(possibleTag =>
      !this.tags.some(tag => tag.Text === possibleTag.Text)  // Compare by text
    );
  }

  onSubmit(): void {
    this.hasError = false;
    this.hasSampleAccessError = false;

    if ( !this.canEditFields ) {
      this.submitSharedStatusOnly();
      return;
    }

    if ( this.leadForm.invalid ) {
      return;
    }

    this.store.dispatch(new ShowLoader());

    const body = this.leadForm.getRawValue();
    const selectedArticles = this.getArticles();
    const articlesToAdd = selectedArticles.filter(it => !it.Id);
    const articlesToRemove = this.previousArticles.filter(prev =>
      !selectedArticles.find(curr => curr.Article === prev.Article)
    );

    if ( this.sameAsContactAddress ) {
      for ( const name of ['Address', 'ZipCode', 'City', 'CountryCode', 'ProvinceCode'] ) {
        delete body[name];
      }
    }

    const observables: Observable<any>[] = [];

    observables.push(this.leadsDataService.updateAddress(this.dialogData.leadId, body));

    if ( this.oldConstructionTypeId !== body.ConstructionTypeId ) {
      observables.push(this.leadsDataService.putLeadById(this.dialogData.leadId, body.ConstructionTypeId));
    }

    if ( this.oldLeadType !== body.LeadTypeId ) {
      observables.push(this.actionDataService.changeType(this.dialogData.leadId, '', body.LeadTypeId));
    }

    if ( this.oldLeadSource !== body.LeadSourceId ) {
      observables.push(this.actionDataService.changeSource(this.dialogData.leadId, body.LeadSourceId));
    }

    if ( [this.leadTypeBrochure, this.leadTypeMuster, this.leadTypeAusschreibungstexte, this.leadTypeCadDaten].includes(body.LeadTypeId) ) {

      if ( articlesToRemove.length ) {
        for ( const article of articlesToRemove ) {
          observables.push(this.leadsDataService.deleteLeadArticle(this.dialogData.leadId, article.Id));
        }
      }

      if ( articlesToAdd.length ) {
        for ( const article of articlesToAdd ) {
          observables.push(this.leadsDataService.addLeadArticle(this.dialogData.leadId, article));
        }
      }
    } else {
      if ( this.previousArticles.length ) {
        for ( const article of this.previousArticles ) {
          observables.push(this.leadsDataService.deleteLeadArticle(this.dialogData.leadId, article.Id));
        }
      }
    }

    if (this.articlesThatRemovedOnSubmit.length ) {
      for ( const article of this.articlesThatRemovedOnSubmit ) {
        observables.push(this.leadsDataService.deleteLeadArticle(this.dialogData.leadId, article.Id));
      }
    }

    if ( this.oldCommission !== body.Commission ) {
      observables.push(this.leadsDataService.updateCommission(this.dialogData.leadId, { Commission: body.Commission }));
    }

    if ( this.oldPlanerNumber !== body.PLANER_CODE ) {
      observables.push(this.leadsDataService.putExtendedPropertiesByLeadId(this.dialogData.leadId, {
        PLANER_CODE: body.PLANER_CODE,
      }));
    }

    if ( this.tagsToAdd.length ) {
      const tagNames = [];
      this.tagsToAdd.map(tag => tagNames.push(tag.Text));
      observables.push(this.leadsDataService.sendKeyWords(tagNames, this.dialogData.leadId));
    }

    if ( this.tagsToDelete.length ) {
      const tagNames = [];
      this.tagsToDelete.map(tag => tagNames.push(tag.Text));
      observables.push(this.leadsDataService.deleteKeywords(tagNames, this.dialogData.leadId));
    }

    if ( this.isShared !== this.oldIsShared ) {
      observables.push(this.actionDataService.updateIsSharedStatus(this.dialogData.leadId, this.isShared));
    }

    const results: any[] = [];

    from(observables).pipe(
      concatMap(obs => obs.pipe(
        tap(result => results.push(result))
      ))
    ).subscribe({
      error: ( err ) => {
        this.store.dispatch(new HideLoader());
        this.hasError = true;
        console.error('Submission failed', err);
      },
      complete: () => {
        this.store.dispatch(new HideLoader());

        for ( const item of results ) {
          if ( !item.success && item.error === 'ERR_REL_LEAD_CHANGE' ) {
            this.hasError = true;
            break;
          }
          if ( !item.success && item.status === 403 ) {
            this.hasSampleAccessError = true;
            break
          }
        }

        if ( !this.hasError && !this.hasSampleAccessError ) {
          const message = (this.tagsToAdd.length || this.tagsToDelete.length) ? 'updateWithTags' : 'update';
          this.onClosePopup(message);
        }
      }
    });
  }

  /**
   * "Share only" mode - the header data itself is read only, so the shared status is the only
   * thing that can be persisted. Nothing to do when it did not change.
   */
  private submitSharedStatusOnly(): void {
    if ( this.isShared === this.oldIsShared ) {
      this.onClosePopup();
      return;
    }

    this.store.dispatch(new ShowLoader());

    this.actionDataService.updateIsSharedStatus(this.dialogData.leadId, this.isShared).subscribe({
      next: ( result ) => {
        this.store.dispatch(new HideLoader());

        if ( !result.success ) {
          this.hasError = true;
          return;
        }

        this.onClosePopup('update');
      },
      error: ( err ) => {
        this.store.dispatch(new HideLoader());
        this.hasError = true;
        console.error('Submission failed', err);
      }
    });
  }

  onCountryChange( countryCode: string ): void {
    this.leadForm.controls.CountryCode.setValue(countryCode);

    AddressHelper.getProvinces(countryCode).then(data => {
      this.resourceProvinces = data;

      if ( !this.resourceProvinces.some(x =>
        x.Code.toLowerCase() === this.leadForm.controls.ProvinceCode.value?.toLowerCase()
      ) ) {
        this.leadForm.controls.ProvinceCode.setValue('');
      }
    })
  }

  onItemChange( data: string[], type: string ): void {
    let list = undefined;
    switch ( type ) {
      case 'brochure':
        list = this.brochures;
        break;
      case 'muster':
        list = this.musters;
        break;
      case 'aussch':
        list = this.ausschreibungstextes;
        break;
      case 'cadDaten':
        list = this.cadDatens;
        break;
    }
    const items = [];
    data.map(( el ) => {
      const item = list.find(b => b.value === el);
      const selectedItem = this.previousArticles.find(it => it.Article === el);
      if ( item ) {
        items.push({
          text: item.text,
          value: item.value,
          id: selectedItem?.Id
        })
      }
    })
    this.selectedItems = items;
  }

  onSameAsContactAddressChange(): void {
    for ( const name of ['Address', 'ZipCode', 'City', 'CountryCode', 'ProvinceCode'] ) {
      this.leadForm.controls[name].setValue(this.sameAsContactAddress ? this.lead?.Contact?.[name] : '');
    }

    this.onCountryChange(this.leadForm.controls.CountryCode.value);
  }

  isInvalidForm( form: string, fieldName: string ): boolean {
    return this.hasError && this[form].controls[fieldName].invalid;
  }

  onChangeSharedStatus() {
  }

  updateLeadTypesDropdown(): void {
    if (!this.selectedLeadTypeId) {
      return;
    }
    if (this.selectedLeadTypeId === this.leadTypeRelationshipManagement) {
      this.leadTypes = this.loadedLeadTypes.filter(l => l.Id === this.leadTypeRelationshipManagement);
    } else {
      this.leadTypes = this.loadedLeadTypes.filter(l => l.Id !== this.leadTypeRelationshipManagement);
    }
  }

  private setSelectedArticles( articles: Articles[], initialLeadTypeId: number ): void {
    if ( !articles ) {
      return;
    }

    const filteredArticles = this.filterArticles(articles, initialLeadTypeId);

    this.previousArticles = filteredArticles;
    filteredArticles.map(( it ) => {
      this.selectedItems.push({
        id: it.Id,
        text: it.Description,
        value: it.Article
      })
    })
  }

  private filterArticles( leadArticles: Articles[], initialLeadTypeId: number ): Articles[] {
    let articles: Articles[] = [];
    if (
      [
        LeadHelper.LEAD_TYPE_BROCHURE,
        LeadHelper.LEAD_TYPE_AUSSCHREIBUNGSTEXTE,
        LeadHelper.LEAD_TYPE_CAD_DATEN,
        LeadHelper.LEAD_TYPE_MUSTER
      ].includes(initialLeadTypeId)
    ) {
      let prefix = 'broch_'

      switch ( initialLeadTypeId ) {
        case LeadHelper.LEAD_TYPE_AUSSCHREIBUNGSTEXTE:
          prefix = 'aussch_';
          break
        case LeadHelper.LEAD_TYPE_CAD_DATEN:
          prefix = 'cad_';
          break
        case LeadHelper.LEAD_TYPE_MUSTER:
          prefix = 'must_';
          break
      }

      for ( const item of leadArticles ) {
        if ( this.articleKeyLeadTypeIds.get(prefix + item.Article) ) {
          articles.push(item);
        } else {
          this.articlesThatRemovedOnSubmit.push(item);
        }
      }
    } else {
      articles = [];
    }
    return articles;
  }

  private getArticles(): Articles[] {
    const articles: Articles[] = [];
    this.selectedItems.forEach(item => {
      articles.push({
        Id: item.id,
        Description: item.text,
        Article: item.value
      });
    });

    return articles;
  }

  private setDynamicLabelKeys(): void {
    for ( const lead of this.loadedLeadTypes ) {
      if ( lead.Id === this.leadTypeBrochure ) {
        this.broschureLabelText = lead.Description;
      }
      if ( lead.Id === this.leadTypeMuster ) {
        this.musterLabelText = lead.Description;
      }
      if ( lead.Id === this.leadTypeAusschreibungstexte ) {
        this.ausschreibungstexteLabelText = lead.Description;
      }
      if ( lead.Id === this.leadTypeCadDaten ) {
        this.cadDateLabelText = lead.Description;
      }
    }
  }

  private checkSharedLead( contactToDisplay: LeadsDataContactObject ): void {
    // TODO verify with product: this used to be `isDealerPartner`, switched to `isPremiumProjectPartner`
    //  to match new-request.component.ts (showSharedLeadCheckbox) and to make sure the checkbox is
    //  available when the overlay is opened in share-only mode. Side effect: a non premium dealer
    //  partner no longer sees the share checkbox while editing. Revert to isDealerPartner if wrong.
    this.showSharedLeadBox = (contactToDisplay.ContactTypeId === ContactHelper.CONTACT_TYPE_PLANER ||
      contactToDisplay.ContactTypeId === ContactHelper.CONTACT_TYPE_TRADE) && this.isPremiumProjectPartner;
  }

  private convertStringToKeyword( data: string[] ): Keyword[] {
    const keywords: Keyword[] = [];
    for ( let i = 0; i < data.length; i++ ) {
      keywords.push({
        Owned: true,
        Text: data[i],
      });
    }
    return keywords;
  }

}
